import { useTheme } from '@emotion/react';
import { useColorMode, useColorInverted, processAutoProps } from '@nettbureau/core';
import { resolveColorAlias } from '@nettbureau/styling';
import { isThemeToken } from '@nettbureau/theme';
import { isString } from 'radash';

/**
 * Processes the props of a shape and returns the processed props.
 *
 * @param props - The shape props to process.
 */ function useShapeProps(props) {
    const isDarkMode = useColorMode() === 'dark';
    const isInverted = useColorInverted();
    const isDark = isInverted || isDarkMode;
    const { borderRadius = props.figure === 'circle' ? 'round' : 'auto', fill = 'base', fillTone = 'medium', height, minHeight, outline = 'auto', outlineTone = 'auto', outlineWidth = isDark && (!props.fillTone || props.fillTone === 'medium' || props.fillTone === 'bold') ? 'regular' : 'none', padding = 'auto', figure = 'rectangle', layout, typography, ...shapeProps } = props;
    const { align = 'auto', gap = 'auto', justify = 'auto', ...layoutProps } = layout ?? {};
    const { color = 'auto', lineHeight = 'auto', shadow = 'auto', size = 'auto', ...typographyProps } = typography ?? {};
    const theme = useTheme();
    const effectiveHeight = height ?? minHeight;
    const heightToken = isThemeToken(effectiveHeight, theme.heights) && effectiveHeight;
    const fillToken = resolveColorAlias(theme, fill);
    const fillToneToken = isString(fillTone) && fillTone;
    const autoProcessors = {
        borderRadius: heightToken ? theme.maps.heightToBorderRadius[heightToken] : undefined,
        padding: heightToken ? [
            `${(theme.heights[heightToken] - theme.heights[theme.maps.heightToContentHeight[heightToken]]) / 2}px`,
            theme.maps.heightToSpacing[heightToken]
        ] : undefined,
        outline: fillToken && fillToneToken ? theme.maps.fillToStrokeColor[fillToken][fillToneToken] : undefined,
        outlineTone: fillToken && fillToneToken ? theme.maps.fillToOutlineColorTone[fillToken][fillToneToken] : undefined
    };
    const layoutAutoProcessors = {
        align: heightToken ? 'center' : undefined,
        gap: heightToken ? theme.maps.heightToGap[heightToken] : undefined,
        justify: figure === 'rectangle' ? 'start' : 'center'
    };
    const typographyAutoProcessors = {
        color: fillToken && fillToneToken ? theme.maps.fillToTextColor[fillToken][fillTone] : undefined,
        lineHeight: heightToken ? `${theme.heights[theme.maps.heightToContentHeight[heightToken]]}px` : undefined,
        shadow: fillToken && fillToneToken ? theme.maps.fillToTextShadow[fillToken][fillTone] : undefined,
        size: heightToken ? theme.maps.heightToFontSize[heightToken] : undefined
    };
    return {
        ...shapeProps,
        ...processAutoProps({
            borderRadius,
            padding,
            outline,
            outlineTone
        }, autoProcessors),
        fill,
        fillTone,
        heightToken,
        height,
        minHeight,
        outlineWidth,
        figure,
        ...layout !== null && {
            layout: {
                ...layoutProps,
                ...processAutoProps({
                    align,
                    gap,
                    justify
                }, layoutAutoProcessors)
            }
        },
        ...typography !== null && {
            typography: {
                ...typographyProps,
                ...processAutoProps({
                    color,
                    lineHeight,
                    shadow,
                    size
                }, typographyAutoProcessors)
            }
        }
    };
}

export { useShapeProps };
//# sourceMappingURL=use-shape-props.js.map
